export interface VersionInfo {
  frontend: {
    version: string
    buildNumber: string
    gitCommit: string
    gitBranch: string
    buildDate: string
    nodeVersion: string
  }
  backend?: {
    version: string
    buildNumber: string
    gitCommit: string
    gitBranch: string
    buildDate: string
    environment: string
    databaseVersion: string
    lastMigration: string
  }
}

export interface HealthInfo {
  status: string
  database: string
  timestamp: string
  version: string
  buildNumber: string
  gitCommit: string
  gitBranch: string
  buildDate: string
  environment: string
  databaseVersion: string
  lastMigration: string
}

class VersionService {
  private static instance: VersionService
  private versionCache: VersionInfo | null = null
  private readonly cacheTimeout = 5 * 60 * 1000 // 5 minutes
  private lastFetch = 0

  public static getInstance(): VersionService {
    if (!VersionService.instance) {
      VersionService.instance = new VersionService()
    }
    return VersionService.instance
  }

  public getFrontendVersion(): VersionInfo['frontend'] {
    return {
      // VITE_APP_VERSION comes from the generated .env at build time; __APP_VERSION__
      // is injected by vite.config.ts straight from package.json so the fallback is
      // never a stale hardcoded number.
      version: import.meta.env.VITE_APP_VERSION || (typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'unknown'),
      buildNumber: import.meta.env.VITE_BUILD_NUMBER || 'dev',
      gitCommit: import.meta.env.VITE_GIT_COMMIT || 'local',
      gitBranch: import.meta.env.VITE_GIT_BRANCH || 'local',
      buildDate: import.meta.env.VITE_BUILD_DATE || new Date().toISOString(),
      nodeVersion: import.meta.env.VITE_NODE_VERSION || 'unknown',
    }
  }

  private async fetchBackendVersion(): Promise<VersionInfo['backend'] | null> {
    // The API exposes no version endpoint; health probes are plain text.
    return null
  }

  public async getFullVersionInfo(): Promise<VersionInfo> {
    const now = Date.now()
    
    // Use cached version if still valid
    if (this.versionCache && (now - this.lastFetch) < this.cacheTimeout) {
      return this.versionCache
    }

    const frontend = this.getFrontendVersion()
    const backend = await this.fetchBackendVersion()

    this.versionCache = {
      frontend,
      ...(backend && { backend })
    }
    this.lastFetch = now

    return this.versionCache
  }

  public async getShortVersionString(): Promise<string> {
    try {
      const version = await this.getFullVersionInfo()
      
      if (version.backend) {
        // Show both frontend and backend versions if backend is available
        const fVersion = version.frontend.version
        const bVersion = version.backend.version
        
        if (fVersion === bVersion) {
          return `v${fVersion}`
        } else {
          return `v${fVersion} (API: ${bVersion})`
        }
      } else {
        // Frontend only
        return `v${version.frontend.version}`
      }
    } catch (error) {
      console.error('Failed to get version string:', error)
      return `v${this.getFrontendVersion().version}`
    }
  }

  public clearCache(): void {
    this.versionCache = null
    this.lastFetch = 0
  }

  // Utility method for displaying version in different formats
  public formatVersion(version: VersionInfo, format: 'short' | 'detailed' = 'short'): string {
    if (format === 'short') {
      return `v${version.frontend.version}`
    }

    let result = `Frontend: v${version.frontend.version}`
    if (version.frontend.gitCommit !== 'local') {
      result += ` (${version.frontend.gitCommit})`
    }

    if (version.backend) {
      result += ` | Backend: v${version.backend.version}`
      if (version.backend.gitCommit !== 'local') {
        result += ` (${version.backend.gitCommit})`
      }
      result += ` | Env: ${version.backend.environment}`
    } else {
      result += ' | Backend: Unavailable'
    }

    return result
  }
}

// Export singleton instance
export const versionService = VersionService.getInstance()
